import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { html, js } from "js-beautify";
import * as Diff from "diff";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, RotateCcw } from "lucide-react";

const SAMPLE_HTML = `<div class="developer-card">
<h1 class="title">Hello, Developer!</h1>
<p class="description">Format and beautify your raw HTML code instantly.</p>
<ul>
<li>100% local processing</li>
<li>No sign-up required</li>
</ul>
</div>`;

export function HtmlFormatter() {
  const [input, setInput] = useState(SAMPLE_HTML);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = (val = input) => {
    setOutput(html(val, { indent_size: 2 }));
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="HTML Formatter & Beautifier Online - Clean HTML Code"
      description="Beautify, format, and clean your messy or minified HTML code instantly with proper indentation. 100% private and runs entirely in your browser."
      h1="HTML Formatter"
      category="Code Formatting"
      categoryPath="/code-formatting"
      howToUse={[
        "Paste your messy, unformatted HTML code into the input area.",
        "Click 'Format HTML Code' to instantly beautify it with clean 2-space indentation.",
        "Copy the neat, structured HTML output or click Clear to start over."
      ]}
      faq={[
        { question: "Why should I use an HTML Formatter?", answer: "An HTML formatter organizes your code nesting, tags, and indentations, making it readable and easier to debug." },
        { question: "Is my HTML code secure on this site?", answer: "Absolutely. All processing occurs locally in your browser memory. No data is sent to our servers." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Button onClick={() => process()} className="bg-slate-900 hover:bg-slate-800 text-white">Format HTML Code</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_HTML); process(SAMPLE_HTML); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Raw HTML</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="<html>..." className="min-h-[350px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Beautified HTML</label>
            <Textarea value={output} readOnly className="min-h-[350px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_JS = `function greetUser(name){console.log("Hello, "+name+"!");const active=true;if(active){return name.toUpperCase();}else{return null;}}`;

export function JsFormatter() {
  const [input, setInput] = useState(SAMPLE_JS);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = (val = input) => {
    setOutput(js(val, { indent_size: 2 }));
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JS & TS Formatter Online - Beautify JavaScript Code"
      description="Pretty-print and format your JavaScript and TypeScript code instantly with beautiful 2-space nesting. Purely client-side and completely private."
      h1="JS/TS Formatter"
      category="Code Formatting"
      categoryPath="/code-formatting"
      howToUse={[
        "Paste your minified or unindented JS or TS script into the editor.",
        "Click 'Format JS/TS Code' to trigger the parser.",
        "Easily inspect the nested structure and copy your clean code."
      ]}
      faq={[
        { question: "Does this support TypeScript?", answer: "Yes, it formats both modern JavaScript and TypeScript code blocks smoothly." },
        { question: "Is this safe to run on enterprise code?", answer: "Yes. All formatting is processed in your local browser sandbox, ensuring absolute data confidentiality." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Button onClick={() => process()} className="bg-slate-900 hover:bg-slate-800 text-white">Format JS/TS Code</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_JS); process(SAMPLE_JS); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Raw JS/TS Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="const a = 1;" className="min-h-[350px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Beautified Output</label>
            <Textarea value={output} readOnly className="min-h-[350px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_MINIFIER = `<div className="card">
  <!-- This is a comment that will be stripped -->
  <h1 className="title">Main Title</h1>
  <p className="paragraph">This tool removes spaces, tabs, and comments to compress files.</p>
</div>`;

export function Minifiers() {
  const [input, setInput] = useState(SAMPLE_MINIFIER);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"html" | "css" | "js">("html");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    let min = val;
    if (currentMode === "html") {
      min = min.replace(/<!--[\s\S]*?-->/g, '').replace(/\s+/g, ' ').replace(/> </g, '><').trim();
    } else if (currentMode === "css") {
      min = min.replace(/\s+/g, ' ').replace(/\/\*.*?\*\//g, '').replace(/\s*([\{?*:;\}])\s*/g, '$1').trim();
    } else if (currentMode === "js") {
      min = min.replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, '').replace(/\s+/g, ' ').replace(/\s*([=\{\}\(\)\[\]\+\-\*\/;,])\s*/g, '$1').trim();
    }
    setOutput(min);
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "html" | "css" | "js") => {
    setMode(newMode);
    if (newMode === "html") {
      setInput(SAMPLE_MINIFIER);
      process(SAMPLE_MINIFIER, "html");
    } else if (newMode === "css") {
      const cssSample = `body { margin: 20px;\n padding: 10px; \n background-color: #ffffff; \n}`;
      setInput(cssSample);
      process(cssSample, "css");
    } else {
      const jsSample = `const sum = (a, b) => {\n  // Add two numbers\n  return a + b;\n};`;
      setInput(jsSample);
      process(jsSample, "js");
    }
  };

  return (
    <ToolPageLayout
      title="Code Minifier Online - Compress HTML, CSS & JS"
      description="Compress and minify your HTML, CSS, and JS files by stripping extra whitespace, newlines, and code comments. Optimize payload sizes instantly."
      h1="Code Minifiers"
      category="Code Formatting"
      categoryPath="/code-formatting"
      howToUse={[
        "Select your code type: HTML, CSS, or JS.",
        "Paste your fully formatted source code into the editor.",
        "The tool will instantly generate a highly compressed, single-line output."
      ]}
      faq={[
        { question: "What is minification?", answer: "Minification removes comments, tabs, and spaces without altering logic, yielding smaller file sizes for quicker website loading." },
        { question: "How does it affect execution?", answer: "The code executes exactly the same way, but downloads faster over networks due to reduced bytes." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("html")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "html" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              HTML
            </button>
            <button 
              onClick={() => handleModeChange("css")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "css" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              CSS
            </button>
            <button 
              onClick={() => handleModeChange("js")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "js" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              JavaScript
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Minified"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Formatted Source Input</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="Paste uncompressed code here..." className="min-h-[350px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Minified & Compressed Output</label>
            <Textarea value={output} readOnly className="min-h-[350px] bg-slate-50 font-mono text-xs text-slate-700 break-all" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_DIFF_1 = `const config = {
  host: "localhost",
  port: 8080,
  debug: true
};`;

const SAMPLE_DIFF_2 = `const config = {
  host: "production-server.local",
  port: 443,
  debug: false,
  ssl: true
};`;

export function CodeDiffViewer() {
  const [text1, setText1] = useState(SAMPLE_DIFF_1);
  const [text2, setText2] = useState(SAMPLE_DIFF_2);
  const [diffResult, setDiffResult] = useState<Diff.Change[]>([]);

  const compare = () => {
    const diff = Diff.diffLines(text1, text2);
    setDiffResult(diff);
  };

  useEffect(() => {
    compare();
  }, []);

  return (
    <ToolPageLayout
      title="Code Diff Viewer Online - Compare Texts"
      description="Compare two code scripts or text files side-by-side. Highlight deletions in red and additions in green instantly."
      h1="Code Diff Viewer"
      category="Code Formatting"
      categoryPath="/code-formatting"
      howToUse={[
        "Paste your original code into the left text area.",
        "Paste your modified code into the right text area.",
        "See line-by-line differences highlighted in the output console below."
      ]}
      faq={[
        { question: "How does the code diff viewer show changes?", answer: "Lines highlighted in red with a '-' prefix represent deletions. Lines in green with a '+' prefix represent additions." },
        { question: "Is my code secure?", answer: "Yes, comparisons are evaluated locally in your browser memory." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-semibold text-slate-500 uppercase">Original Code</label>
              <Button variant="ghost" size="sm" onClick={() => { setText1(SAMPLE_DIFF_1); setTimeout(compare, 50); }} className="text-xs h-6 py-0">Load Sample</Button>
            </div>
            <Textarea value={text1} onChange={e => { setText1(e.target.value); }} className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-semibold text-slate-500 uppercase">Modified Code</label>
              <Button variant="ghost" size="sm" onClick={() => { setText2(SAMPLE_DIFF_2); setTimeout(compare, 50); }} className="text-xs h-6 py-0">Load Sample</Button>
            </div>
            <Textarea value={text2} onChange={e => { setText2(e.target.value); }} className="min-h-[220px] font-mono text-xs" />
          </div>
        </div>
        <Button onClick={compare} className="w-full bg-slate-900 hover:bg-slate-800 text-white">Compare Code Lines Now</Button>
        {diffResult.length > 0 && (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Visual Differences</label>
            <div className="p-4 border rounded-xl bg-slate-900 text-slate-100 min-h-[150px] whitespace-pre font-mono text-xs overflow-x-auto leading-relaxed shadow-xs">
              {diffResult.map((part, index) => {
                const color = part.added ? "bg-green-950/75 text-green-400 border-l-4 border-green-500 pl-2 py-0.5" : part.removed ? "bg-red-950/75 text-red-400 border-l-4 border-red-500 pl-2 py-0.5" : "text-slate-300 pl-3";
                const prefix = part.added ? "+ " : part.removed ? "- " : "  ";
                return (
                  <div key={index} className={color}>
                    {part.value.split('\n').filter((l, i, a) => i !== a.length-1 || l).map((line, i) => (
                      <div key={i} className="flex"><span className="select-none opacity-50 mr-2">{prefix}</span><span>{line}</span></div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function DevTools1() {
  return (
    <ToolPageLayout
      title="Web Development & Formatting Tools Online"
      description="Explore high-speed browser tools to format HTML, format JS/TS, minify static assets, or view code diffs side-by-side."
      h1="Web Development Tools"
      category="Dev Tools"
      categoryPath="/dev-tools"
      howToUse={[
        "Choose any of the specialized modules below for formatting or code comparisons.",
        "All calculations run in secure local RAM sandbox memory."
      ]}
      faq={[
        { question: "Where do these tools execute?", answer: "Entirely in your local browser sandbox, using Privacy Shield." }
      ]}
    >
      <div className="space-y-16">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">HTML Formatter</h2><HtmlFormatter /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">JS/TS Formatter</h2><JsFormatter /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Code Minifiers</h2><Minifiers /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Code Diff Viewer</h2><CodeDiffViewer /></section>
      </div>
    </ToolPageLayout>
  );
}
